import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HomeIcon } from "../../Components/Icons/HomeIcon";
import { useTranslation } from "react-i18next";
import AppointmentsService from "../../Api/Services/AppointmentsService";
import type { AppointmentsModel } from "../../Api/Models/appointments.model";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";



const Appointments: React.FunctionComponent = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const navigate = useNavigate();

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [appointments, setAppointments] = useState<AppointmentsModel[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterRiderId, setFilterRiderId] = useState("");


    const fetchAppointments = async (date: Date) => {
        const formattedDate = format(date, "yyyy-MM-dd");
        try {
            setLoading(true);
            const response = await AppointmentsService.getAppointments(formattedDate);
            setAppointments(response);
        } catch (error) {
            console.error("Failed to fetch appointments", error);
        } finally {
            setLoading(false);
        }
    };


    const [selectedAppointment, setSelectedAppointment] = useState<AppointmentsModel | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        fetchAppointments(selectedDate);
    }, [selectedDate]);

    const handleMissed = async (appointmentId: number) => {
        try {
            await AppointmentsService.missedAppointments(appointmentId);
            setIsModalOpen(false);
            fetchAppointments(selectedDate);
        } catch (error) {
            console.error(error);
        }
    };

    const today = new Date();
    const isToday =
        selectedDate.getFullYear() === today.getFullYear() &&
        selectedDate.getMonth() === today.getMonth() &&
        selectedDate.getDate() === today.getDate();

    const filteredAppointments = appointments.filter(appt =>
        appt.rider_id.toString().includes(filterRiderId.trim())
    );

    return (
        <div className="view-container background-white">
            <div className="view-content">
                <div className="navigationbar navigationbar--dark mb-6">
                    <div className="navigationbar__left">
                        <Link to="/menu" className="navigationbar__button">
                            <HomeIcon />
                        </Link>
                    </div>
                    <div className="navigationbar__center">
                        <div className="navigationbar__title">{t("Appointments")}</div>
                    </div>
                    <div className="navigationbar__right" />
                </div>
                <div className="mb-6 flex justify-center">
                    <div className="relative flex items-center border border-gray-300 rounded-lg px-4 py-2 shadow-sm bg-white">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-500 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>

                        <DatePicker
                            selected={selectedDate}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    setSelectedDate(date);
                                }
                            }}
                            dateFormat="yyyy-MM-dd"
                            className="outline-none w-40 text-sm"
                            locale={lang}
                        />
                    </div>
                </div>
                <div className="mb-6 flex justify-center">
                    <input
                        type="text"
                        value={filterRiderId}
                        onChange={(e) => setFilterRiderId(e.target.value)}
                        placeholder={t("Filter by Rider ID") as string}
                        className="border border-gray-300 rounded-lg px-4 py-2 shadow-sm w-60"
                    />
                </div>

                {filteredAppointments.length === 0 ? (
                    <div className="text-center text-gray-400 mt-10">
                        {t("No appointments found for this date.")}
                    </div>
                ) : (
                    <div className="card-list mt-4">
                        {filteredAppointments.map((appt) => {
                            const isDisabled =
                                !isToday || appt.appointment_type === "cancelled";

                            return (
                                <div
                                    key={appt.id}
                                    className={`bg-white shadow-sm rounded-xl p-4 mb-4 border border-gray-200 transition ${
                                        isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-md"
                                    }`}
                                    onClick={() => {
                                        if (!isDisabled) {
                                            setSelectedAppointment(appt);
                                            setIsModalOpen(true);
                                        }
                                    }}
                                >
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="font-semibold text-lg">{appt.rider_name} • {appt.rider_id}</div>
                                            <div className="text-sm text-gray-500">
                                                {appt.date} • {appt.appointment_type}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700 uppercase tracking-wide">
                                                {appt.action_type}
                                            </span>
                                            {appt.im_here && appt.is_today ? (
                                            <span className="text-xs px-3 py-1 ml-2 rounded-full bg-green-600 text-yellow-400 uppercase tracking-wide">

                                            </span>
                                            ) : null }
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="flex justify-center mt-6">
                    <button
                        onClick={() => fetchAppointments(selectedDate)}
                        className={`button w-full ${
                            loading ? "bg-gray-400 cursor-not-allowed" : "text-white"
                        }`}
                        disabled={loading}
                    >
                        {loading && (
                            <svg
                                className="animate-spin h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a12 12 0 00-12 12h4z"></path>
                            </svg>
                        )}
                        {loading ? t("Refreshing...") : t("Refresh")}
                    </button>
                </div>
            </div>
            {isModalOpen && selectedAppointment && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md mx-4">
                        <h2 className="text-lg font-semibold mb-4">{t("Select action for")} {selectedAppointment.rider_name}</h2>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={async () => {
                                    try {
                                        await AppointmentsService.completeAppointment(selectedAppointment.id);
                                        setIsModalOpen(false);
                                        navigate(`/rider/${selectedAppointment.rider_id}/profile`);
                                    } catch (error) {
                                        console.error("Failed to mark as viewed", error);
                                    }
                                }}
                                className="w-full px-4 py-2 text-center bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                            >
                                {t("Action")}
                            </button>
                            <button
                                className="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                                onClick={() => handleMissed(selectedAppointment.id)}
                            >
                                {t("Missed")}
                            </button>
                            <button
                                className="w-full px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                                onClick={() => setIsModalOpen(false)}
                            >
                                {t("Cancel")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Appointments;